/**
 * S4-T06 — Shared route-label resolver.
 *
 * Maps an arbitrary in-app pathname to a translation key under the
 * `common:routeLabels.*` namespace, with sensible fallbacks for unmapped
 * routes. Used by the Recents drawer to render human-readable navigation
 * history.
 *
 * Keep this list in lock-step with the primary nav. When adding a route
 * surface, add a corresponding label key in `public/locales/{en,ar}/common.json`
 * under `routeLabels.*` and an entry here.
 */

const ROUTE_LABEL_MAP: Record<string, string> = {
  // Top-level modules
  '/dashboard': 'routeLabels.dashboard',
  '/finance': 'routeLabels.finance',
  '/engineering': 'routeLabels.engineering',
  '/operations': 'routeLabels.operations',
  '/contracts': 'routeLabels.contracts',
  '/hr': 'routeLabels.hr',
  '/marketing': 'routeLabels.marketing',
  '/legal': 'routeLabels.legal',
  '/workflows': 'routeLabels.workflows',
  '/settings': 'routeLabels.settings',
  '/about': 'routeLabels.about',
  // Finance
  '/finance/coa': 'routeLabels.financeCoa',
  '/finance/gl': 'routeLabels.financeGl',
  '/finance/ap': 'routeLabels.financeAp',
  '/finance/ar': 'routeLabels.financeAr',
  '/finance/cash-flow': 'routeLabels.financeCashFlow',
  '/finance/budgets': 'routeLabels.financeBudgets',
  '/finance/fixed-assets': 'routeLabels.financeFixedAssets',
  '/finance/payroll': 'routeLabels.financePayroll',
  '/finance/vat': 'routeLabels.financeVat',
  // Contracts
  '/contracts/profitability': 'routeLabels.contractsProfitability',
  '/contracts/variation-orders': 'routeLabels.contractsVo',
  '/contracts/ipc': 'routeLabels.contractsIpc',
  '/contracts/po': 'routeLabels.contractsPo',
  '/contracts/claims': 'routeLabels.contractsClaims',
  '/contracts/correspondence': 'routeLabels.contractsCorrespondence',
  '/contracts/rfq': 'routeLabels.contractsRfq',
  '/contracts/rfp': 'routeLabels.contractsRfp',
  // Engineering
  '/engineering/projects': 'routeLabels.engineeringProjects',
  '/engineering/drawings': 'routeLabels.engineeringDrawings',
  '/engineering/submittals': 'routeLabels.engineeringSubmittals',
  '/engineering/material-requests': 'routeLabels.engineeringMrs',
  '/engineering/estimates': 'routeLabels.engineeringEstimates',
  '/engineering/boq': 'routeLabels.engineeringBoq',
  '/engineering/bim': 'routeLabels.engineeringBim',
  // HR
  '/hr/employees': 'routeLabels.hrEmployees',
  '/hr/org-chart': 'routeLabels.hrOrgChart',
  '/hr/payroll': 'routeLabels.hrPayroll',
  '/hr/document-expiry': 'routeLabels.hrDocumentExpiry',
  '/hr/leave': 'routeLabels.hrLeave',
  '/hr/attendance': 'routeLabels.hrAttendance',
  '/hr/recruitment': 'routeLabels.hrRecruitment',
  // Marketing
  '/marketing/leads': 'routeLabels.marketingLeads',
  '/marketing/tenders': 'routeLabels.marketingTenders',
  '/marketing/reports': 'routeLabels.marketingReports',
  '/marketing/clients': 'routeLabels.marketingClients',
  // Settings
  '/settings/audit': 'routeLabels.settingsAudit',
  '/settings/users': 'routeLabels.settingsUsers',
  '/settings/integrations': 'routeLabels.settingsIntegrations',
};

/**
 * Resolves a pathname to a translation key. Falls back to:
 *   1. Exact match in ROUTE_LABEL_MAP
 *   2. Longest-prefix match (so /engineering/projects/p-01 → engineeringProjects)
 *   3. The path itself, prettified (caller renders raw)
 */
export function resolveRouteLabelKey(path: string): string | null {
  if (ROUTE_LABEL_MAP[path]) return ROUTE_LABEL_MAP[path];

  const segments = path.split('/').filter(Boolean);
  while (segments.length > 1) {
    segments.pop();
    const prefix = '/' + segments.join('/');
    if (ROUTE_LABEL_MAP[prefix]) return ROUTE_LABEL_MAP[prefix];
  }

  return null;
}
